import React, { useState, useEffect } from "react";
import { db, storage } from "../firebase";
import { collection, addDoc, getDocs, updateDoc, doc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import "./AddContentModal.css";

function AddContentModal({ isOpen, onClose, onContentAdded }) {
  const [selectedType, setSelectedType] = useState("Image");
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [playlists, setPlaylists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState("");
  const [duration, setDuration] = useState(10); // Default duration for images and documents
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState("");

  // Fetch playlists from Firestore
  useEffect(() => {
    const fetchPlaylists = async () => {
      try {
        const playlistsRef = collection(db, "playlists");
        const querySnapshot = await getDocs(playlistsRef);
        const playlistData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setPlaylists(playlistData);
      } catch (err) {
        console.error("Error fetching playlists:", err.message);
      }
    };

    fetchPlaylists();
  }, []);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name || !file) {
      setError("Please provide a name and select a file.");
      return;
    }

    setIsUploading(true);
    const fileRef = ref(storage, `media/${selectedType}/${Date.now()}-${file.name}`);

    try {
      // Upload file to Firebase Storage
      await uploadBytes(fileRef, file);
      const fileUrl = await getDownloadURL(fileRef);

      // Add file to Firestore
      const mediaData = {
        name,
        mediaType: selectedType.toLowerCase(),
        url: fileUrl,
        duration: selectedType !== "Video" ? duration : null, // Duration only for images and documents
        addedToPlaylist: selectedPlaylist || null,
        uploadedAt: new Date(),
      };

      const mediaRef = collection(db, "media");
      const docRef = await addDoc(mediaRef, mediaData);

      console.log("Media uploaded with ID:", docRef.id);

      // Optionally, add the media to the selected playlist
      if (selectedPlaylist) {
        const playlistDoc = playlists.find((pl) => pl.id === selectedPlaylist);

        if (playlistDoc) {
          const updatedMedia = playlistDoc.media ? [...playlistDoc.media, docRef.id] : [docRef.id];
          await updateDoc(doc(db, "playlists", selectedPlaylist), { media: updatedMedia });
          console.log("Media added to playlist:", selectedPlaylist);
        }
      }

      // Notify the parent component
      if (onContentAdded) {
        onContentAdded();
      }

      onClose();
    } catch (err) {
      console.error("Error uploading media:", err.message);
      setError("Failed to upload content. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <h2>Add Content</h2>
        <form onSubmit={handleSubmit}>
          {/* Media Type Selection */}
          <div className="media-type-selection">
            {["Image", "Video", "Document"].map((type) => (
              <button
                type="button"
                key={type}
                className={`media-type-button ${selectedType === type ? "selected" : ""}`}
                onClick={() => setSelectedType(type)}
              >
                {type}
              </button>
            ))}
          </div>

          {/* Name Input */}
          <div className="form-group">
            <label>Name:</label>
            <input
              type="text"
              value={name}
              placeholder="Enter file name"
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          {/* File Upload */}
          <div className="form-group">
            <label>File:</label>
            <div className="file-upload">
              <input type="file" onChange={handleFileChange} required />
              {file && <span className="file-name">{file.name}</span>}
            </div>
          </div>

          {/* Duration Input for Image/Document */}
          {(selectedType === "Image" || selectedType === "Document") && (
            <div className="form-group">
              <label>Default Duration (seconds):</label>
              <input
                type="number"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                min="1"
                required
              />
            </div>
          )}

          {/* Playlist Selection */}
          <div className="form-group">
            <label>Add to Playlist:</label>
            <select
              value={selectedPlaylist}
              onChange={(e) => setSelectedPlaylist(e.target.value)}
            >
              <option value="">None</option>
              {playlists.map((playlist) => (
                <option key={playlist.id} value={playlist.id}>
                  {playlist.name}
                </option>
              ))}
            </select>
          </div>

          {/* Actions */}
          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn cancel">
              Cancel
            </button>
            <button type="submit" className="btn save" disabled={isUploading}>
              {isUploading ? "Uploading..." : "Save"}
            </button>
          </div>
        </form>
        {error && <p className="error-message">{error}</p>}
      </div>
    </div>
  );
}

export default AddContentModal;
